import { DeleteOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Card, Col, Image, notification, Row, Typography } from 'antd';
import useModal from 'antd/lib/modal/useModal';
import { NoteInput } from 'components/Create/FormItems/NoteInput';
import { AttachmentUpload } from 'components/Create/MoreDetails';
import { useFormikContext } from 'formik';
import { DeploymentRequestLabels, DeploymentRequestPayload } from 'models/DeploymentRequest';
import { QueryErrorModel } from 'models/ErrorModel';
import { useParams } from 'react-router-dom';
import { useDeleteAttachmentMutation, useGetSparingRequestQuery } from 'redux/services/twittlebugs/sparingRequestsApi';
import { colors, toRgba } from 'styles/colors';
const { Title } = Typography;

export const RequestInfoPage = (): JSX.Element => {
  const { id } = useParams();
  const { setFieldValue } = useFormikContext<DeploymentRequestPayload>();

  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [modal, context] = useModal();

  const { data: deploymentRequest } = useGetSparingRequestQuery(id ?? skipToken);

  const handleOk = async (fileName: string): Promise<void> => {
    if (!id)
      return notification.error({
        message: 'Error',
        description: 'An error occured, the team has been notified.',
        className: 'custom-class',
        style: {
          width: 600
        },
        duration: 5
      });
    try {
      await deleteAttachment({ id, fileName }).unwrap();
      notification.success({
        message: 'Suceess',
        description: 'Successfully deleted attachment.',
        className: 'custom-class',
        style: {
          width: 600
        },
        duration: 5
      });
    } catch (e) {
      const error = e as QueryErrorModel;

      return notification.error({
        message: 'Error',
        description: error?.data?.errorMessage ?? 'An error occured, the team has been notified.',
        className: 'custom-class',
        style: {
          width: 600
        },
        duration: 5
      });
    }
  };

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} md={12} style={{ minHeight: '100%' }}>
        <Card
          style={{ height: '100%' }}
          headStyle={{ backgroundColor: toRgba(colors.romanSilver, 0.4) }}
          title={
            <Title level={4} style={{ fontWeight: 'normal' }}>
              Add File Attachments
            </Title>
          }>
          <Row style={{ marginTop: 5 }}>
            <Col span={24} style={{ minHeight: '100%' }}>
              <AttachmentUpload />
            </Col>
          </Row>
        </Card>
      </Col>
      {id && (
        <Col xs={24} md={12}>
          <Card
            headStyle={{ backgroundColor: toRgba(colors.romanSilver, 0.4) }}
            bodyStyle={{ minHeight: 259 }}
            title={
              <Title level={4} style={{ fontWeight: 'normal' }}>
                Existing File Attachments
              </Title>
            }>
            <Row style={{ marginTop: 5 }}>
              <Col span={24} style={{ minHeight: '100%' }}>
                {context}
                {deploymentRequest?.attachments.map((attachment) => (
                  <Card bodyStyle={{ padding: 8 }} key={attachment.name} style={{ marginBottom: 5 }}>
                    <Row align="middle" justify="space-between">
                      <Col>
                        <Image
                          fallback={
                            'data:image/svg+xml;base64,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'
                          }
                          width={48}
                          height={48}
                          src={(attachment as unknown as { url: string }).url}
                        />
                        <Typography.Link href={(attachment as unknown as { url: string }).url} style={{ marginLeft: 15 }}>
                          {attachment.name}
                        </Typography.Link>
                      </Col>
                      <DeleteOutlined
                        onClick={(): void => {
                          modal.confirm({
                            content: 'Are you sure you want to delete this attachment?',
                            onOk: (): Promise<void> => handleOk(attachment.name)
                          });
                        }}
                      />
                    </Row>
                  </Card>
                ))}
              </Col>
            </Row>
          </Card>
        </Col>
      )}
      <Col xs={24} md={12}>
        <Card
          headStyle={{ backgroundColor: toRgba(colors.royalBlueLight, 0.4) }}
          title={
            <Title level={4} style={{ fontWeight: 'normal' }}>
              Order Details
            </Title>
          }
          style={{ height: '100%' }}>
          <Row justify="space-between" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Col span={24}>
              <NoteInput fieldName="notes" label={DeploymentRequestLabels.note} placeholder="Note message" />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
};
